import { wordlist } from "./wordlist.ts";

export type Separator = "-" | "." | "_" | "!" | "?" | " ";

export type PassphraseOptions = {
	readonly wordCount: number;
	readonly capitalize: boolean;
	readonly includeNumber: boolean;
	readonly separator: Separator;
};

export type StrengthLevel = "Weak" | "Fair" | "Strong" | "Very Strong";

export type StrengthInfo = {
	readonly level: StrengthLevel;
	readonly percent: number;
	readonly color: string;
};

/**
 * Generate a cryptographically random integer in the range [0, max).
 */
function secureRandomInt(max: number): number {
	const array = new Uint32Array(1);
	crypto.getRandomValues(array);
	return (array[0] as number) % max;
}

/**
 * Pick a random word from the wordlist.
 */
function randomWord(): string {
	return wordlist[secureRandomInt(wordlist.length)] as string;
}

/**
 * Capitalize the first letter of a word.
 */
function capitalizeWord(word: string): string {
	return word.charAt(0).toUpperCase() + word.slice(1);
}

/**
 * Generate a passphrase with the given options.
 */
export function generatePassphrase(options: PassphraseOptions): string {
	const { wordCount, capitalize, includeNumber, separator } = options;

	const words: string[] = [];
	for (let i = 0; i < wordCount; i++) {
		let word = randomWord();
		if (capitalize) {
			word = capitalizeWord(word);
		}
		words.push(word);
	}

	if (includeNumber) {
		const position = secureRandomInt(words.length);
		const digit = secureRandomInt(10);
		words[position] = `${words[position]}${digit}`;
	}

	return words.join(separator);
}

/**
 * Estimate passphrase entropy in bits.
 */
export function estimateEntropy(options: PassphraseOptions): number {
	const poolSize = wordlist.length;
	let bitsPerWord = Math.log2(poolSize);

	if (options.capitalize) {
		// capitalization adds ~1 bit per word (2 cases)
		bitsPerWord += 1;
	}

	let totalBits = bitsPerWord * options.wordCount;

	if (options.includeNumber) {
		// adds log2(wordCount * 10) bits
		totalBits += Math.log2(options.wordCount * 10);
	}

	return totalBits;
}

/**
 * Determine passphrase strength from options.
 */
export function getStrength(options: PassphraseOptions): StrengthInfo {
	const entropy = estimateEntropy(options);

	if (entropy >= 80) {
		return { level: "Very Strong", percent: 100, color: "#16a34a" };
	}
	if (entropy >= 60) {
		return {
			level: "Strong",
			percent: 75,
			color: "#65a30d",
		};
	}
	if (entropy >= 40) {
		return { level: "Fair", percent: 50, color: "#eab308" };
	}
	return { level: "Weak", percent: 25, color: "#dc2626" };
}
